import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@itaas/core/auth/useAuth";
import { hydrateBillingKanakkuData } from "@/billing/data/billing-data-store";
import { BillingDataContextProvider, useBillingDataVersion } from "@/billing/BillingDataContext";

interface BillingDataProviderProps {
  children: ReactNode;
}

function BillingDataLoader({ children }: BillingDataProviderProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { version, bump } = useBillingDataVersion();
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    await hydrateBillingKanakkuData(true);
    bump();
  }, [bump]);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      setReady(true);
      return;
    }

    let cancelled = false;
    setReady(false);

    const timeout = window.setTimeout(() => {
      if (!cancelled) {
        console.warn("[billing] hydrate timeout — affichage sans données");
        setReady(true);
      }
    }, 15000);

    refresh()
      .catch((err) => {
        console.error("[billing] hydrate failed", err);
      })
      .finally(() => {
        window.clearTimeout(timeout);
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [isAuthenticated, isLoading, refresh]);

  if (isLoading || (isAuthenticated && !ready)) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <BillingDataContextProvider version={version} refresh={refresh}>
      {children}
    </BillingDataContextProvider>
  );
}

export function BillingDataProvider({ children }: BillingDataProviderProps) {
  return <BillingDataLoader>{children}</BillingDataLoader>;
}
